import React, { useCallback } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { noop } from 'ui-components/default-props/noop';

import { EditContainer } from './components/edit-container';
import { ReadOnlyOtherShippingOptionsWrapper } from './components/read-only-container';
import { OtherShippingOptionsHeaderPills } from './header-pills';
import { useOtherShippingOptionsStyleContext } from './style-provider';

import style from './other-shipping-options.module.css';
import { emptyObject } from 'ui-components/default-props/empty';

export const messages = defineMessages({
  title: {
    description: 'orderDrawer.otherShippingOptions.title',
    defaultMessage: 'Other Shipping Options',
    id: 'w4yka4',
  },
});

const getActiveOptions = (options) => {
  const activeOptions = [];
  const optionKeys = Object.keys(options);
  optionKeys.forEach((option) => {
    if (options[option].checked) {
      activeOptions.push(option);
    }
  });
  return activeOptions;
};

export const OtherShippingOptions = ({
  open = false,
  onClick = noop,
  options = emptyObject,
  fulfillmentPlanId,
  editableFulfillmentPlanId,
  dragHandleProps,
  serviceId = '',
  className = null,
  headerClassName = null,
  noIcon = false,
  hideOtherShippingOptions = false,
  saveOptions,
}) => {
  const { classes: contextClasses } = useOtherShippingOptionsStyleContext();

  const headerClickHandler = useCallback(() => {
    onClick(open);
  }, [onClick, open]);

  const classes = classnames(style['other-options-wrapper'], className, contextClasses.collapsibleSection);

  return (
    <>
      {!hideOtherShippingOptions && (
        <CollapsibleListSection className={classes} open={open}>
          <CollapsibleListSectionHeader
            caretClassName={contextClasses.collapsibleSectionCaretIcon}
            className={classnames(style['other-options-header'], headerClassName)}
            contentClassName={contextClasses.collapsibleSectionHeaderContent}
            dragHandleProps={dragHandleProps}
            grabberIconClassName={contextClasses.collapsibleSectionGrabberIcon}
            noIcon={noIcon}
            onClick={headerClickHandler}
            theme='white'
          >
            <FormattedMessage {...messages.title} />
            <OtherShippingOptionsHeaderPills
              activeOptions={getActiveOptions(options)}
              className={contextClasses.headerPillsContainer}
              fulfillmentPlanId={fulfillmentPlanId}
              pillClassName={contextClasses.headerPill}
            />
          </CollapsibleListSectionHeader>
          <CollapsibleListSectionContent>
            <div className={classnames(style['options-section'], contextClasses.collapsibleSectionContent)}>
              {editableFulfillmentPlanId ? (
                <EditContainer
                  key={`${serviceId}-${editableFulfillmentPlanId}`}
                  fulfillmentPlanId={editableFulfillmentPlanId}
                  options={options}
                  saveOptions={saveOptions}
                />
              ) : (
                <ReadOnlyOtherShippingOptionsWrapper fulfillmentPlanId={fulfillmentPlanId} optionData={options} />
              )}
            </div>
          </CollapsibleListSectionContent>
        </CollapsibleListSection>
      )}
    </>
  );
};

OtherShippingOptions.propTypes = {
  open: PropTypes.bool,
  onClick: PropTypes.func,
  options: PropTypes.object,
  fulfillmentPlanId: PropTypes.string,
  editableFulfillmentPlanId: PropTypes.string,
  dragHandleProps: PropTypes.object,
  serviceId: PropTypes.string,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  noIcon: PropTypes.bool,
  hideOtherShippingOptions: PropTypes.bool,
  saveOptions: PropTypes.func.isRequired,
};
